import { Component, Input, OnInit } from '@angular/core';
import { ITaskInfo } from '../../../interfaces/task-info';
import moment from 'moment';
import { IAdditionalChargeFilter } from '../../../interfaces/additional-charge-filter';
import { JobStatus } from '../../../enums/job-status';
import { AdditionalChargeTypeEnum } from '../../../enums/additional-charge-type.enum';
import { AdditionalChargeStatusEnum } from '../../../enums/additional-charge.enum';
import { AdditionalChargeService, FileStorageService } from '../../../services';
import { ISupplierInformationAdditionalCharge } from '../../../interfaces/additional-charge';
import { FormControl, FormGroup } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'supplier-charges-information',
  templateUrl: './supplier-charges-information.component.html',
  styleUrls: ['./supplier-charges-information.component.scss'],
})
export class SupplierChargesInformationComponent implements OnInit {
  //Pending = 1,
  //Completed = 2,
  //Declined = 3
  @Input('supplierId') supplierId: number;
  @Input('status') status: number;

  jobStatusOptions: { value: any; label: string }[] = [];
  jobStatusNames: { [key: number]: string } = {};
  additionalChargeTypeOptions: { label: string; value: any }[] = [];
  additionalChargeStatusOptions: { label: string; value: any }[] = [];
  additionalChargeStatusNames: { [key: number]: string } = {};
  customerOptions: { label: string; value: any }[] = [];
  charges: ISupplierInformationAdditionalCharge[] = [];
  isShownPopupPhoto: boolean = false;
  loadingPopupPhoto: boolean = false;
  allowClosePopupPhoto: boolean = false;
  popupPhotoUrl: string;
  additionalChargeStatusEnum = AdditionalChargeStatusEnum;
  selectedAdditionalCharge: any | boolean;
  jobInfo: any;
  jobStatus = JobStatus;
  isLoading = true;
  filterForm: FormGroup;
  filteredCharges: ISupplierInformationAdditionalCharge[] = [];
  selectedChargeId: number;
  selectedJobId: number;

  sortByOptions: { label: string; value: string }[] = [
    { label: 'Sort By', value: '' },
    { label: 'Recent Job Completed', value: 'recentJobCompleted' },
    { label: 'Recent Expense Logged', value: 'recentExpenseLogged' },
    { label: 'Amount Descending', value: 'amountDescending' },
    { label: 'Amount Ascending', value: 'amountAscending' },
  ];

  constructor(
    private chargesService: AdditionalChargeService,
    private fileStorageService: FileStorageService,
  ) {
    this.initForm();

    this.jobStatusOptions = (
      Object.keys(JobStatus)
        .map((k) => JobStatus[k])
        .filter((v) => typeof v === 'string') as string[]
    )
      .map((n) => ({
        label: n,
        value: JobStatus[n],
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    this.jobStatusNames = this.jobStatusOptions.reduce((acc, curr) => {
      acc[curr.value] = curr.label;
      return acc;
    });

    this.additionalChargeTypeOptions = (
      Object.keys(AdditionalChargeTypeEnum)
        .map((k) => AdditionalChargeTypeEnum[k])
        .filter((v) => typeof v === 'string') as string[]
    )
      .map((n) => ({
        label: n,
        value: AdditionalChargeTypeEnum[n],
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    this.additionalChargeStatusOptions = (
      Object.keys(AdditionalChargeStatusEnum)
        .map((k) => AdditionalChargeStatusEnum[k])
        .filter((v) => typeof v === 'string') as string[]
    )
      .map((n) => ({
        label: n,
        value: AdditionalChargeStatusEnum[n],
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    this.additionalChargeStatusNames = this.additionalChargeStatusOptions.reduce((acc, curr) => {
      acc[curr.value] = curr.label;
      return acc;
    });

    this.jobStatusOptions.unshift({ value: null, label: 'All Job Statuses' });
    this.additionalChargeTypeOptions.unshift({ value: null, label: 'All Charge Types' });
    this.additionalChargeStatusOptions.unshift({ value: null, label: 'All Charge Statuses' });
  }

  async ngOnInit() {
    let filters: IAdditionalChargeFilter = {
      filters: {
        supplierId: { value: this.supplierId },
      },
    };

    this.chargesService.getSupplierInformationAdditionalCosts(filters).then((result) => {
      console.log('Loaded charges: ', result);
      this.charges = result.items;
      this.isLoading = false;
      this.initCustomers();
      this.updateTable();
    });
  }

  resetForm() {
    this.initForm();
    this.updateTable();
  }

  initForm() {
    this.filterForm = new FormGroup({
      vehicleReg: new FormControl(''),
      jobNumber: new FormControl(''),
      jobCompletedDate: new FormControl(null),
      jobStatusId: new FormControl(null),
      additionalChargeTypeId: new FormControl(null),
      customerId: new FormControl(null),
      expenseStatusId: new FormControl(null),
      sortBy: new FormControl(''),
    });

    this.filterForm.valueChanges.pipe(debounceTime(200)).subscribe((value) => {
      this.updateTable();
    });
  }

  openPhotoPresigned(photoUrl) {
    this.loadingPopupPhoto = true;

    this.fileStorageService.getPreSignedURLs([photoUrl]).then((url) => {
      this.popupPhotoUrl = url.length ? url[0] : null;
      this.isShownPopupPhoto = true;
      this.loadingPopupPhoto = false;
    });
  }

  formatDate(value, format) {
    return moment(value).format(format);
  }

  private initCustomers() {
    // From this.charges get a unique list of charge.task.customerInfo.name
    // and assign it to this.customerOptions
    this.customerOptions = this.charges
      .map((x) => ({ label: x.customerName, value: x.customerId }))
      .filter((v, i, a) => a.findIndex((t) => t.value === v.value) === i);

    this.customerOptions.unshift({ label: 'All Customers', value: null });
  }

  get f() {
    return this.filterForm.value;
  }

  private updateTable() {
    let fromDate = null;
    let toDate = null;

    if (this.f.jobCompletedDate != null) {
      fromDate = moment(this.f.jobCompletedDate[0]).format('YYYY-MM-DD');
      toDate = moment(this.f.jobCompletedDate[1] ?? this.f.jobCompletedDate[0]).format(
        'YYYY-MM-DD',
      );
    }

    this.filteredCharges = this.charges.filter((x) => {
      return (
        (this.f.jobStatusId == null || x.jobStatusId == this.f.jobStatusId) &&
        (this.f.vehicleReg.length == 0 ||
          x.vehicleReg.toLowerCase().includes(this.f.vehicleReg.toLowerCase())) &&
        (this.f.jobNumber.length == 0 ||
          x.jobNumber.toLowerCase().includes(this.f.jobNumber.toLowerCase())) &&
        (this.f.additionalChargeTypeId == null ||
          x.additionalChargeTypeId == this.f.additionalChargeTypeId) &&
        (this.f.customerId == null || x.customerId == this.f.customerId) &&
        (this.f.expenseStatusId == null || x.expenseStatusId == this.f.expenseStatusId) &&
        (fromDate == null ||
          (moment(x.jobCompletedDate).isSameOrAfter(fromDate) &&
            moment(x.jobCompletedDate).isSameOrBefore(toDate)))
      );
    });

    // Apply Sorting

    if (this.f.sortBy === 'recentJobCompleted') {
      this.filteredCharges = this.filteredCharges.sort((a, b) => {
        return moment(a.jobCompletedDate).diff(moment(b.jobCompletedDate));
      });
    } else if (this.f.sortBy == 'recentExpenseLogged') {
      this.filteredCharges = this.filteredCharges.sort((a, b) => {
        return moment(a.dateLogged).diff(moment(b.dateLogged));
      });
    } else if (this.f.sortBy == 'amountDescending') {
      this.filteredCharges = this.filteredCharges.sort((a, b) => {
        return b.amount - a.amount;
      });
    } else if (this.f.sortBy == 'amountAscending') {
      this.filteredCharges = this.filteredCharges.sort((a, b) => {
        return a.amount - b.amount;
      });
    }
  }

  selectCharge(charge: ISupplierInformationAdditionalCharge) {
    this.selectedChargeId = charge.id;
    this.selectedJobId = charge.jobId;
    console.log('GET JOB');
  }
}
